import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Search, Plus, Star, Tv, Film, Info, ChevronDown, ArrowUp, ArrowDown, ListOrdered } from 'lucide-react';

export function SearchSeriesDialog({ 
  open, 
  onClose, 
  searchQuery, 
  setSearchQuery, 
  onSearch, 
  searching, 
  searchResults, 
  onAddSeries,
  onViewDetails,
  mediaType,
  setMediaType,
  onQuickAdd,
  lockedMediaType // If set, hides the toggle and forces this media type
}) {
  // Use locked media type if provided
  const effectiveMediaType = lockedMediaType || mediaType;
  const showMediaToggle = !lockedMediaType;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="search-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit text-[#3D4F6F]">
            {lockedMediaType 
              ? `Add ${lockedMediaType === 'movie' ? 'Movie' : 'TV Show'}` 
              : 'Add to Watchlist'}
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            {lockedMediaType 
              ? `Search for ${lockedMediaType === 'movie' ? 'movies' : 'TV shows'} to add`
              : 'Search for TV shows or movies'}
          </DialogDescription>
        </DialogHeader>
        
        {/* Media Type Toggle - only show if not locked */}
        {showMediaToggle && (
          <Tabs value={mediaType} onValueChange={setMediaType} className="mb-4">
            <TabsList className="grid w-full grid-cols-2 bg-slate-100">
              <TabsTrigger 
                value="tv" 
                className="data-[state=active]:bg-[#F87171] data-[state=active]:text-white rounded-full"
                data-testid="tab-tv"
              >
                <Tv className="w-4 h-4 mr-2" />
                TV Shows
              </TabsTrigger>
              <TabsTrigger 
                value="movie" 
                className="data-[state=active]:bg-[#F87171] data-[state=active]:text-white rounded-full"
                data-testid="tab-movie"
              >
                <Film className="w-4 h-4 mr-2" />
                Movies
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        
        <div className="flex gap-2 mb-4">
          <Input 
            data-testid="search-input" 
            placeholder={`Search by title, actor, or director...`}
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            onKeyDown={(e) => e.key === 'Enter' && onSearch(effectiveMediaType)} 
            className="flex-1" 
          />
          <Button 
            data-testid="search-btn" 
            onClick={() => onSearch(effectiveMediaType)} 
            disabled={searching} 
            className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
          >
            {searching ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
          </Button>
        </div>
        
        <ScrollArea className="h-[400px]">
          {searchResults.length > 0 ? (
            <div className="space-y-3">
              {searchResults.map((result, index) => (
                <div 
                  key={`${result.media_type}-${result.tmdb_id}`} 
                  data-testid={`search-result-${index}`} 
                  className="flex gap-4 p-3 rounded-xl hover:bg-slate-50 group"
                >
                  {result.poster_path ? (
                    <img 
                      src={result.poster_path} 
                      alt={result.title} 
                      className="w-16 h-24 object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => onViewDetails(result)}
                    />
                  ) : (
                    <div 
                      className="w-16 h-24 bg-slate-200 rounded-lg flex items-center justify-center cursor-pointer"
                      onClick={() => onViewDetails(result)}
                    >
                      {result.media_type === 'movie' ? (
                        <Film className="w-6 h-6 text-slate-400" />
                      ) : (
                        <Tv className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-[#3D4F6F]">
                        {result.title}
                      </h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        result.media_type === 'movie' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {result.media_type === 'movie' ? 'Movie' : 'TV'}
                      </span>
                    </div>
                    {result.first_air_date && (
                      <p className="text-sm text-[#64748B]">
                        {new Date(result.first_air_date).getFullYear()}
                      </p>
                    )}
                    {result.person_credit && (
                      <p className="text-xs text-purple-600 mt-0.5 font-medium">
                        {result.person_credit}
                      </p>
                    )}
                    {result.vote_average > 0 && (
                      <div className="flex items-center gap-2 text-sm text-[#64748B] mt-1">
                        <img 
                          src="https://upload.wikimedia.org/wikipedia/commons/6/69/IMDB_Logo_2016.svg" 
                          alt="IMDb" 
                          className="h-3"
                        />
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                          {result.vote_average.toFixed(1)}
                        </span>
                      </div>
                    )}
                    <p className="text-sm text-[#94A3B8] line-clamp-2 mt-1">{result.overview}</p>
                  </div>
                  <div className="flex flex-col gap-2 justify-center">
                    <Button 
                      data-testid={`view-info-btn-${index}`}
                      variant="outline"
                      size="sm"
                      className="rounded-full border-[#3D4F6F] text-[#3D4F6F] hover:bg-[#3D4F6F] hover:text-white"
                      onClick={() => onViewDetails(result)}
                    >
                      <Info className="w-4 h-4 mr-1" />
                      Info
                    </Button>
                    
                    {/* Quick Add Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          data-testid={`add-to-list-btn-${index}`}
                          size="sm"
                          className="rounded-full bg-[#F87171] hover:bg-[#EF4444] text-white"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                          <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem 
                          onClick={() => onQuickAdd ? onQuickAdd(result, 'top') : onAddSeries(result)}
                          className="cursor-pointer"
                          data-testid={`quick-add-top-${index}`}
                        >
                          <ArrowUp className="w-4 h-4 mr-2 text-green-600" />
                          Add to Top (#1)
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => onQuickAdd ? onQuickAdd(result, 'bottom') : onAddSeries(result)}
                          className="cursor-pointer"
                          data-testid={`quick-add-bottom-${index}`}
                        >
                          <ArrowDown className="w-4 h-4 mr-2 text-blue-600" />
                          Add to Bottom
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => onAddSeries(result)}
                          className="cursor-pointer"
                          data-testid={`add-and-rank-${index}`}
                        >
                          <ListOrdered className="w-4 h-4 mr-2 text-[#F87171]" />
                          Add & Rank...
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          ) : searchQuery && !searching ? (
            <div className="text-center py-8 text-[#64748B]">
              No {effectiveMediaType === 'movie' ? 'movies' : 'TV shows'} found.
            </div>
          ) : (
            <div className="text-center py-8 text-[#64748B]">
              Search for {effectiveMediaType === 'movie' ? 'movies' : 'TV shows'} to add
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
